import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { 
  User, Settings, Shield, Eye, EyeOff, Music, Image, 
  Palette, Type, Heart, Calendar, MapPin, Link as LinkIcon,
  Plus, X, Save, ArrowLeft, Lock, Users, Bell, Globe
} from 'lucide-react';
import LanguageSelector from '../components/LanguageSelector';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL;

const THEME_PRESETS = [
  { name: 'Classic Blue', primary: '#003366', secondary: '#0066cc', accent: '#ff6600' },
  { name: 'Emo Black', primary: '#1a1a1a', secondary: '#333333', accent: '#ff0066' },
  { name: 'Scene Pink', primary: '#ff69b4', secondary: '#ff1493', accent: '#00ff00' },
  { name: 'Ocean Teal', primary: '#008080', secondary: '#20b2aa', accent: '#ffd700' },
  { name: 'Purple Haze', primary: '#4b0082', secondary: '#8a2be2', accent: '#00ffff' },
  { name: 'Forest Green', primary: '#228b22', secondary: '#32cd32', accent: '#ffa500' },
  { name: 'Sunset Orange', primary: '#ff4500', secondary: '#ff6347', accent: '#ffff00' },
  { name: 'Midnight', primary: '#191970', secondary: '#000080', accent: '#c0c0c0' },
];

const FONTS = [
  { name: 'Default', value: null },
  { name: 'Comic Sans', value: "'Comic Sans MS', cursive" },
  { name: 'Georgia', value: 'Georgia, serif' },
  { name: 'Courier', value: "'Courier New', monospace" },
  { name: 'Impact', value: 'Impact, sans-serif' },
  { name: 'Trebuchet', value: "'Trebuchet MS', sans-serif" },
  { name: 'Verdana', value: 'Verdana, sans-serif' },
];

const RELATIONSHIP_STATUSES = [
  { label: 'Not specified', value: null },
  { label: 'Single', value: 'single' },
  { label: 'In a relationship', value: 'in_relationship' },
  { label: 'Engaged', value: 'engaged' },
  { label: 'Married', value: 'married' },
  { label: 'It\'s complicated', value: 'complicated' },
  { label: 'In an open relationship', value: 'open_relationship' },
  { label: 'Widowed', value: 'widowed' },
  { label: 'Separated', value: 'separated' },
  { label: 'Divorced', value: 'divorced' },
];

export default function ProfileSettings() {
  const navigate = useNavigate();
  const { user, setUser } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('basic');
  const [usernameAvailable, setUsernameAvailable] = useState(null);
  const [checkingUsername, setCheckingUsername] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    username: '',
    bio: '',
    looking_for: '',
    interests: [],
    birthday: '',
    show_age: false,
    relationship_status: null,
    // Visual
    theme_primary: '#003366',
    theme_secondary: '#0066cc',
    theme_accent: '#ff6600',
    custom_font: null,
    profile_music_url: '',
    profile_music_title: '',
    background_image: '',
    picture: '',
    cover_photo: '',
    // Custom sections
    custom_sections: [],
    featured_photos: [],
    pinned_post_id: '',
    // Social links
    social_links: {},
    // Privacy
    profile_visibility: 'public',
    hide_online_status: false,
    hide_friend_list: false,
  });

  const [newInterest, setNewInterest] = useState('');
  const [newSection, setNewSection] = useState({ title: '', content: '' });
  const [newPhotoUrl, setNewPhotoUrl] = useState('');

  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || '',
        username: user.username || '',
        bio: user.bio || '',
        looking_for: user.looking_for || '',
        interests: user.interests || [],
        birthday: user.birthday || '',
        show_age: user.show_age || false,
        relationship_status: user.relationship_status || null,
        theme_primary: user.theme_primary || '#003366',
        theme_secondary: user.theme_secondary || '#0066cc',
        theme_accent: user.theme_accent || '#ff6600',
        custom_font: user.custom_font || null,
        profile_music_url: user.profile_music_url || '',
        profile_music_title: user.profile_music_title || '',
        background_image: user.background_image || '',
        picture: user.picture || '',
        cover_photo: user.cover_photo || '',
        custom_sections: user.custom_sections || [],
        featured_photos: user.featured_photos || [],
        pinned_post_id: user.pinned_post_id || '',
        social_links: user.social_links || {},
        profile_visibility: user.profile_visibility || 'public',
        hide_online_status: user.hide_online_status || false,
        hide_friend_list: user.hide_friend_list || false,
      });
    }
  }, [user]);

  const handleUsernameChange = async (value) => {
    const cleaned = value.toLowerCase().replace(/[^a-z0-9_]/g, '');
    setFormData({ ...formData, username: cleaned });
    
    if (cleaned.length >= 3 && cleaned !== user?.username) {
      setCheckingUsername(true);
      try {
        const res = await axios.get(`${API}/api/username/check/${cleaned}`);
        setUsernameAvailable(res.data.available);
      } catch {
        setUsernameAvailable(null);
      }
      setCheckingUsername(false);
    } else if (cleaned === user?.username) {
      setUsernameAvailable(true);
    } else {
      setUsernameAvailable(null);
    }
  };

  const handleSave = async () => {
    if (!user) return;
    
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(`${API}/api/user/profile`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
      toast.success('Profile updated successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const addInterest = () => {
    if (newInterest.trim() && !formData.interests.includes(newInterest.trim())) {
      setFormData({ ...formData, interests: [...formData.interests, newInterest.trim()] });
      setNewInterest('');
    }
  };

  const removeInterest = (interest) => {
    setFormData({ ...formData, interests: formData.interests.filter(i => i !== interest) });
  };

  const addCustomSection = () => {
    if (newSection.title.trim() && newSection.content.trim()) {
      setFormData({
        ...formData,
        custom_sections: [...formData.custom_sections, { ...newSection }]
      });
      setNewSection({ title: '', content: '' });
    }
  };

  const removeCustomSection = (index) => {
    setFormData({
      ...formData,
      custom_sections: formData.custom_sections.filter((_, i) => i !== index)
    });
  };

  const addFeaturedPhoto = () => {
    if (newPhotoUrl.trim() && !formData.featured_photos.includes(newPhotoUrl.trim())) {
      setFormData({
        ...formData,
        featured_photos: [...formData.featured_photos, newPhotoUrl.trim()]
      });
      setNewPhotoUrl('');
    }
  };

  const removeFeaturedPhoto = (url) => {
    setFormData({
      ...formData,
      featured_photos: formData.featured_photos.filter(p => p !== url)
    });
  };

  const applyThemePreset = (preset) => {
    setFormData({
      ...formData,
      theme_primary: preset.primary,
      theme_secondary: preset.secondary,
      theme_accent: preset.accent
    });
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <p>Please log in to access settings</p>
      </div>
    );
  }

  const tabs = [
    { id: 'basic', label: 'Basic Info', icon: User },
    { id: 'visual', label: 'Appearance', icon: Palette },
    { id: 'content', label: 'Content', icon: Type },
    { id: 'privacy', label: 'Privacy', icon: Shield },
    { id: 'language', label: 'Language', icon: Globe },
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b-2 border-black sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate(-1)}
              className="border-2 border-black rounded-full"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="text-xl font-bold">Profile Settings</h1>
          </div>
          <Button
            onClick={handleSave}
            disabled={loading}
            className="bg-indigo-500 hover:bg-indigo-600 text-white border-2 border-black rounded-full font-bold shadow-brutal"
          >
            <Save className="w-4 h-4 mr-2" />
            {loading ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6">
        {/* Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {tabs.map(tab => (
            <Button
              key={tab.id}
              variant={activeTab === tab.id ? 'default' : 'outline'}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 rounded-full border-2 border-black ${
                activeTab === tab.id 
                  ? 'bg-indigo-500 text-white' 
                  : 'bg-white hover:bg-slate-100'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </Button>
          ))}
        </div>

        {/* Basic Info Tab */}
        {activeTab === 'basic' && (
          <div className="space-y-6">
            <div className="bg-white border-2 border-black rounded-2xl p-6 shadow-brutal">
              <h2 className="text-lg font-bold mb-4 flex items-center gap-2">
                <User className="w-5 h-5" /> Profile Information
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="font-bold">Handle (public name)</Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">@</span>
                    <Input
                      value={formData.username}
                      onChange={(e) => handleUsernameChange(e.target.value)}
                      className="mt-1 border-2 border-black rounded-xl pl-8"
                    />
                  </div>
                  <p className="text-xs text-slate-500 mt-1">Do not use your real name or variations of it.</p>
                  {checkingUsername && <p className="text-xs text-slate-500 mt-1">Checking...</p>}
                  {usernameAvailable === true && formData.username !== user?.username && (
                    <p className="text-xs text-emerald-600 mt-1">✓ Available</p>
                  )}
                  {usernameAvailable === false && (
                    <p className="text-xs text-red-600 mt-1">✗ Taken</p>
                  )}
                </div>
                
                <div>
                  <Label className="font-bold">Real name (private)</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="mt-1 border-2 border-black rounded-xl"
                  />
                  <p className="text-xs text-slate-500 mt-1">Only visible on your profile. Never shown publicly.</p>
                </div>
              </div>

              <div className="mt-4">
                <div className="md:col-span-2">
                  <Label className="font-bold">Bio</Label>
                  <Textarea
                    value={formData.bio}
                    onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                    placeholder="Tell friends about yourself..."
                    className="mt-1 border-2 border-black rounded-xl min-h-[100px]"
                  />
                </div>

                <div className="md:col-span-2">
                  <Label className="font-bold">Who I&apos;d Like to Meet</Label>
                  <Textarea
                    value={formData.looking_for}
                    onChange={(e) => setFormData({ ...formData, looking_for: e.target.value })}
                    placeholder="Describe the kind of friends you want to connect with..."
                    className="mt-1 border-2 border-black rounded-xl"
                  />
                </div>
              </div>
            </div>

            {/* Personal Details */}
            <div className="bg-white border-2 border-black rounded-2xl p-6 shadow-brutal">
              <h2 className="text-lg font-bold mb-4 flex items-center gap-2">
                <Heart className="w-5 h-5" /> Personal Details
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="font-bold">Birthday</Label>
                  <Input
                    type="date"
                    value={formData.birthday}
                    onChange={(e) => setFormData({ ...formData, birthday: e.target.value })}
                    className="mt-1 border-2 border-black rounded-xl"
                  />
                </div>
                
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={formData.show_age}
                      onCheckedChange={(checked) => setFormData({ ...formData, show_age: checked })}
                    />
                    <Label>Show age on profile</Label>
                  </div>
                </div>

                <div>
                  <Label className="font-bold">Relationship Status</Label>
                  <select
                    value={formData.relationship_status || ''}
                    onChange={(e) => setFormData({ ...formData, relationship_status: e.target.value || null })}
                    className="mt-1 w-full h-10 border-2 border-black rounded-xl px-3 bg-white"
                  >
                    {RELATIONSHIP_STATUSES.map(status => (
                      <option key={status.value || 'none'} value={status.value || ''}>
                        {status.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Interests */}
            <div className="bg-white border-2 border-black rounded-2xl p-6 shadow-brutal">
              <h2 className="text-lg font-bold mb-4">Interests</h2>
              
              <div className="flex flex-wrap gap-2 mb-4">
                {formData.interests.map(interest => (
                  <span
                    key={interest}
                    className="px-3 py-1 bg-indigo-100 text-indigo-700 rounded-full text-sm flex items-center gap-1"
                  >
                    {interest}
                    <button onClick={() => removeInterest(interest)} className="hover:text-red-500">
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
              
              <div className="flex gap-2">
                <Input
                  value={newInterest}
                  onChange={(e) => setNewInterest(e.target.value)}
                  placeholder="Add an interest..."
                  className="border-2 border-black rounded-xl"
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addInterest())}
                />
                <Button onClick={addInterest} className="border-2 border-black rounded-xl">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {/* Social Links */}
            <div className="bg-white border-2 border-black rounded-2xl p-6 shadow-brutal">
              <h2 className="text-lg font-bold mb-4 flex items-center gap-2">
                <LinkIcon className="w-5 h-5" /> Social Links
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {['instagram', 'twitter', 'tiktok', 'youtube', 'linkedin', 'website'].map(platform => (
                  <div key={platform}>
                    <Label className="font-bold capitalize">{platform}</Label>
                    <Input
                      value={formData.social_links[platform] || ''}
                      onChange={(e) => setFormData({
                        ...formData,
                        social_links: { ...formData.social_links, [platform]: e.target.value }
                      })}
                      placeholder={`Your ${platform} URL`}
                      className="mt-1 border-2 border-black rounded-xl"
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Visual/Appearance Tab */}
        {activeTab === 'visual' && (
          <div className="space-y-6">
            {/* Profile Photos */}
            <div className="bg-white border-2 border-black rounded-2xl p-6 shadow-brutal">
              <h2 className="text-lg font-bold mb-4 flex items-center gap-2">
                <Image className="w-5 h-5" /> Profile Photos
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="font-bold">Profile Picture URL</Label>
                  <Input
                    value={formData.picture}
                    onChange={(e) => setFormData({ ...formData, picture: e.target.value })}
                    placeholder="https://..."
                    className="mt-1 border-2 border-black rounded-xl"
                  />
                  {formData.picture && (
                    <img src={formData.picture} alt="Preview" className="mt-2 w-20 h-20 rounded-full object-cover border-2 border-black" />
                  )}
                </div>
                
                <div>
                  <Label className="font-bold">Cover Photo URL</Label>
                  <Input
                    value={formData.cover_photo}
                    onChange={(e) => setFormData({ ...formData, cover_photo: e.target.value })}
                    placeholder="https://..."
                    className="mt-1 border-2 border-black rounded-xl"
                  />
                  {formData.cover_photo && (
                    <img src={formData.cover_photo} alt="Preview" className="mt-2 w-full h-20 rounded-xl object-cover border-2 border-black" />
                  )}
                </div>

                <div className="md:col-span-2">
                  <Label className="font-bold">Background Image URL</Label>
                  <Input
                    value={formData.background_image}
                    onChange={(e) => setFormData({ ...formData, background_image: e.target.value })}
                    placeholder="https://... (optional pattern/image for profile background)"
                    className="mt-1 border-2 border-black rounded-xl"
                  />
                </div>
              </div>
            </div>

            {/* Theme Colors */}
            <div className="bg-white border-2 border-black rounded-2xl p-6 shadow-brutal">
              <h2 className="text-lg font-bold mb-4 flex items-center gap-2">
                <Palette className="w-5 h-5" /> Theme Colors
              </h2>
              
              <div className="mb-4">
                <Label className="font-bold mb-2 block">Presets</Label>
                <div className="flex flex-wrap gap-2">
                  {THEME_PRESETS.map(preset => (
                    <button
                      key={preset.name}
                      onClick={() => applyThemePreset(preset)}
                      className="px-3 py-2 rounded-full border-2 border-black text-sm font-medium flex items-center gap-2 hover:opacity-80"
                      style={{ backgroundColor: preset.primary, color: '#fff' }}
                    >
                      <span className="w-3 h-3 rounded-full" style={{ backgroundColor: preset.accent }} />
                      {preset.name}
                    </button>
                  ))}
                </div>
              </div>
              
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label className="font-bold">Primary</Label>
                  <div className="flex gap-2 items-center mt-1">
                    <input
                      type="color"
                      value={formData.theme_primary}
                      onChange={(e) => setFormData({ ...formData, theme_primary: e.target.value })}
                      className="w-10 h-10 rounded border-2 border-black cursor-pointer"
                    />
                    <Input
                      value={formData.theme_primary}
                      onChange={(e) => setFormData({ ...formData, theme_primary: e.target.value })}
                      className="border-2 border-black rounded-xl flex-1"
                    />
                  </div>
                </div>
                <div>
                  <Label className="font-bold">Secondary</Label>
                  <div className="flex gap-2 items-center mt-1">
                    <input
                      type="color"
                      value={formData.theme_secondary}
                      onChange={(e) => setFormData({ ...formData, theme_secondary: e.target.value })}
                      className="w-10 h-10 rounded border-2 border-black cursor-pointer"
                    />
                    <Input
                      value={formData.theme_secondary}
                      onChange={(e) => setFormData({ ...formData, theme_secondary: e.target.value })}
                      className="border-2 border-black rounded-xl flex-1"
                    />
                  </div>
                </div>
                <div>
                  <Label className="font-bold">Accent</Label>
                  <div className="flex gap-2 items-center mt-1">
                    <input
                      type="color"
                      value={formData.theme_accent}
                      onChange={(e) => setFormData({ ...formData, theme_accent: e.target.value })}
                      className="w-10 h-10 rounded border-2 border-black cursor-pointer"
                    />
                    <Input
                      value={formData.theme_accent}
                      onChange={(e) => setFormData({ ...formData, theme_accent: e.target.value })}
                      className="border-2 border-black rounded-xl flex-1"
                    />
                  </div>
                </div>
              </div>
              
              {/* Preview */}
              <div className="mt-4 p-4 rounded-xl border-2 border-black" style={{ backgroundColor: formData.theme_primary }}>
                <p style={{ color: formData.theme_accent }} className="font-bold">Preview Text</p>
                <p style={{ color: formData.theme_secondary }}>Secondary color text</p>
              </div>
            </div>

            {/* Font & Music */}
            <div className="bg-white border-2 border-black rounded-2xl p-6 shadow-brutal">
              <h2 className="text-lg font-bold mb-4 flex items-center gap-2">
                <Type className="w-5 h-5" /> Font & Music
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="font-bold">Custom Font</Label>
                  <select
                    value={formData.custom_font || ''}
                    onChange={(e) => setFormData({ ...formData, custom_font: e.target.value || null })}
                    className="mt-1 w-full h-10 border-2 border-black rounded-xl px-3 bg-white"
                    style={{ fontFamily: formData.custom_font || 'inherit' }}
                  >
                    {FONTS.map(font => (
                      <option key={font.name} value={font.value || ''} style={{ fontFamily: font.value || 'inherit' }}>
                        {font.name}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <Label className="font-bold flex items-center gap-2">
                    <Music className="w-4 h-4" /> Profile Song Title
                  </Label>
                  <Input
                    value={formData.profile_music_title}
                    onChange={(e) => setFormData({ ...formData, profile_music_title: e.target.value })}
                    placeholder="Song name - Artist"
                    className="mt-1 border-2 border-black rounded-xl"
                  />
                </div>
                
                <div className="md:col-span-2">
                  <Label className="font-bold">Profile Music URL (YouTube, Spotify, etc.)</Label>
                  <Input
                    value={formData.profile_music_url}
                    onChange={(e) => setFormData({ ...formData, profile_music_url: e.target.value })}
                    placeholder="https://..."
                    className="mt-1 border-2 border-black rounded-xl"
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Content Tab */}
        {activeTab === 'content' && (
          <div className="space-y-6">
            {/* Custom Sections */}
            <div className="bg-white border-2 border-black rounded-2xl p-6 shadow-brutal">
              <h2 className="text-lg font-bold mb-4">Custom Sections</h2>
              <p className="text-sm text-slate-600 mb-4">Add custom sections like &quot;Favorite Quotes&quot;, &quot;Currently Watching&quot;, etc.</p>
              
              {formData.custom_sections.map((section, index) => (
                <div key={index} className="mb-4 p-4 bg-slate-50 rounded-xl border-2 border-slate-200">
                  <div className="flex justify-between items-start">
                    <div>
                      <h4 className="font-bold">{section.title}</h4>
                      <p className="text-sm text-slate-600">{section.content}</p>
                    </div>
                    <button onClick={() => removeCustomSection(index)} className="text-red-500 hover:text-red-700">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
              
              <div className="space-y-2">
                <Input
                  value={newSection.title}
                  onChange={(e) => setNewSection({ ...newSection, title: e.target.value })}
                  placeholder="Section title (e.g., 'Favorite Movies')"
                  className="border-2 border-black rounded-xl"
                />
                <Textarea
                  value={newSection.content}
                  onChange={(e) => setNewSection({ ...newSection, content: e.target.value })}
                  placeholder="Section content..."
                  className="border-2 border-black rounded-xl"
                />
                <Button onClick={addCustomSection} className="border-2 border-black rounded-xl">
                  <Plus className="w-4 h-4 mr-2" /> Add Section
                </Button>
              </div>
            </div>

            {/* Featured Photos */}
            <div className="bg-white border-2 border-black rounded-2xl p-6 shadow-brutal">
              <h2 className="text-lg font-bold mb-4">Featured Photos</h2>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                {formData.featured_photos.map((url, index) => (
                  <div key={index} className="relative group">
                    <img src={url} alt={`Featured ${index + 1}`} className="w-full h-24 object-cover rounded-xl border-2 border-black" />
                    <button
                      onClick={() => removeFeaturedPhoto(url)}
                      className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
              
              <div className="flex gap-2">
                <Input
                  value={newPhotoUrl}
                  onChange={(e) => setNewPhotoUrl(e.target.value)}
                  placeholder="Photo URL..."
                  className="border-2 border-black rounded-xl"
                />
                <Button onClick={addFeaturedPhoto} className="border-2 border-black rounded-xl">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {/* Pinned Post */}
            <div className="bg-white border-2 border-black rounded-2xl p-6 shadow-brutal">
              <h2 className="text-lg font-bold mb-4">Pinned Post</h2>
              <p className="text-sm text-slate-600 mb-4">Enter a post ID to pin to your profile</p>
              
              <Input
                value={formData.pinned_post_id}
                onChange={(e) => setFormData({ ...formData, pinned_post_id: e.target.value })}
                placeholder="Post ID (e.g., post_abc123)"
                className="border-2 border-black rounded-xl"
              />
            </div>
          </div>
        )}

        {/* Privacy Tab */}
        {activeTab === 'privacy' && (
          <div className="space-y-6">
            <div className="bg-white border-2 border-black rounded-2xl p-6 shadow-brutal">
              <h2 className="text-lg font-bold mb-4 flex items-center gap-2">
                <Shield className="w-5 h-5" /> Privacy Settings
              </h2>
              
              <div className="space-y-6">
                <div>
                  <Label className="font-bold mb-2 block">Profile Visibility</Label>
                  <div className="space-y-2">
                    {[
                      { value: 'public', label: 'Public', desc: 'Anyone can view your full profile', icon: Eye },
                      { value: 'friends_only', label: 'Friends Only', desc: 'Only friends can see your full profile', icon: Users },
                      { value: 'private', label: 'Private', desc: 'Only you can see your profile', icon: Lock },
                    ].map(option => (
                      <label
                        key={option.value}
                        className={`flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer transition-colors ${
                          formData.profile_visibility === option.value
                            ? 'border-indigo-500 bg-indigo-50'
                            : 'border-slate-200 hover:border-slate-300'
                        }`}
                      >
                        <input
                          type="radio"
                          name="visibility"
                          value={option.value}
                          checked={formData.profile_visibility === option.value}
                          onChange={(e) => setFormData({ ...formData, profile_visibility: e.target.value })}
                          className="sr-only"
                        />
                        <option.icon className="w-5 h-5 text-slate-600" />
                        <div>
                          <p className="font-medium">{option.label}</p>
                          <p className="text-sm text-slate-500">{option.desc}</p>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                    <div className="flex items-center gap-3">
                      <EyeOff className="w-5 h-5 text-slate-600" />
                      <div>
                        <p className="font-medium">Hide Online Status</p>
                        <p className="text-sm text-slate-500">Others won&apos;t see when you&apos;re online</p>
                      </div>
                    </div>
                    <Switch
                      checked={formData.hide_online_status}
                      onCheckedChange={(checked) => setFormData({ ...formData, hide_online_status: checked })}
                    />
                  </div>

                  <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                    <div className="flex items-center gap-3">
                      <Users className="w-5 h-5 text-slate-600" />
                      <div>
                        <p className="font-medium">Hide Friend List</p>
                        <p className="text-sm text-slate-500">Others won&apos;t see your friends</p>
                      </div>
                    </div>
                    <Switch
                      checked={formData.hide_friend_list}
                      onCheckedChange={(checked) => setFormData({ ...formData, hide_friend_list: checked })}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Blocked Users */}
            <div className="bg-white border-2 border-black rounded-2xl p-6 shadow-brutal">
              <h2 className="text-lg font-bold mb-4 flex items-center gap-2">
                <Lock className="w-5 h-5" /> Blocked Users
              </h2>
              <p className="text-sm text-slate-600 mb-4">
                Manage users you&apos;ve blocked. Blocked users cannot view your profile or message you.
              </p>
              <Button
                variant="outline"
                onClick={() => navigate('/settings/blocked')}
                className="border-2 border-black rounded-xl"
              >
                Manage Blocked Users
              </Button>
            </div>
          </div>
        )}

        {/* Language Tab */}
        {activeTab === 'language' && (
          <div className="space-y-6">
            <div className="bg-white border-2 border-black rounded-2xl p-6 shadow-brutal">
              <h2 className="text-lg font-bold mb-4 flex items-center gap-2">
                <Globe className="w-5 h-5" /> Language Settings
              </h2>
              <p className="text-sm text-slate-600 mb-6">
                Choose your preferred language. The app will be translated using Google Translate.
              </p>
              
              <LanguageSelector />
              
              <div className="mt-6 p-4 bg-indigo-50 rounded-xl border border-indigo-200">
                <p className="text-sm text-indigo-700">
                  <strong>Supported Languages:</strong> English, Spanish, French, German, Portuguese, Chinese, Japanese, Italian, Korean, Arabic, Hindi, Russian, Dutch
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
